import { env } from './env';
import { ApiError, type Envelope, unwrapList, unwrapOk, type ListResult } from './envelope';

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  method?: Method;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/**
 * Hook seam — set this from auth bootstrap to inject a fresh Firebase ID token.
 * Returning null means unauthenticated.
 */
let getAuthToken: () => Promise<string | null> = async () => null;

export function setAuthTokenProvider(provider: () => Promise<string | null>): void {
  getAuthToken = provider;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(path.startsWith('/') ? path : `/${path}`, env.VITE_API_BASE_URL);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function rawRequest<TEnvelope>(path: string, options: RequestOptions): Promise<{ envelope: TEnvelope; status: number }> {
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const url = buildUrl(path, options.query);
  let response: Response;
  try {
    response = await fetch(url, {
      method: options.method ?? 'GET',
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') throw err;
    throw new ApiError('NETWORK_ERROR', err instanceof Error ? err.message : 'Network request failed', 0, err);
  }

  let envelope: TEnvelope;
  try {
    envelope = (await response.json()) as TEnvelope;
  } catch {
    throw new ApiError('ENVELOPE_INVALID', `Non-JSON response (${response.status})`, response.status);
  }
  return { envelope, status: response.status };
}

export const apiClient = {
  async get<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    const { envelope, status } = await rawRequest<Envelope<T>>(path, { ...options, method: 'GET' });
    return unwrapOk(envelope, status);
  },
  async getList<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<ListResult<T>> {
    const { envelope, status } = await rawRequest<Envelope<T>>(path, { ...options, method: 'GET' });
    return unwrapList(envelope, status);
  },
  async post<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    const { envelope, status } = await rawRequest<Envelope<T>>(path, { ...options, method: 'POST', body });
    return unwrapOk(envelope, status);
  },
  async put<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    const { envelope, status } = await rawRequest<Envelope<T>>(path, { ...options, method: 'PUT', body });
    return unwrapOk(envelope, status);
  },
  async patch<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    const { envelope, status } = await rawRequest<Envelope<T>>(path, { ...options, method: 'PATCH', body });
    return unwrapOk(envelope, status);
  },
  async delete<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
    const { envelope, status } = await rawRequest<Envelope<T>>(path, { ...options, method: 'DELETE' });
    return unwrapOk(envelope, status);
  },
};
